// Karttapohjat. Maanmittauslaitoksen maastokartta ja taustakartta tulevat
// oman palvelimen kautta (backend/app/maptiles.py), joka säilyttää MML:n
// API-avaimen ja karttaruutujen välimuistin. OpenStreetMap on varapohja,
// jos palvelimelle ei ole vielä asetettu MML:n avainta.

import L from "leaflet";

import { getServerSettings } from "./api.js";
import { serverIdentity, sessionHeaders } from './accountSession.js';
import { loadTile, MAX_NATIVE_ZOOM, tileCache } from "./tileCache.js";

const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const MML_ATTRIBUTION = '&copy; <a href="https://www.maanmittauslaitos.fi/">Maanmittauslaitos</a>, CC BY 4.0';

export const BASEMAPS = [
  { id: "maastokartta", name: "Maastokartta", hint: "Korkeuskäyrät, polut ja maasto", proxied: true },
  { id: "taustakartta", name: "Taustakartta", hint: "Vaalea ja selkeä, ruudut erottuvat hyvin", proxied: true },
  { id: "osm", name: "OpenStreetMap", hint: "Kadut ja palvelut", proxied: false },
];

/**
 * Valitsee näytettävän pohjan: käyttäjän valinta, jos se on palvelimella
 * käytettävissä, muuten OpenStreetMap. Kun palvelimen tasoja ei vielä
 * tiedetä (null), palautetaan null: näin käynnistyksessä ei välähdä väärä
 * kartta eikä ladata turhaan OpenStreetMapin ruutuja.
 */
export function effectiveBasemap(chosen, serverLayers) {
  const wanted = BASEMAPS.find((b) => b.id === chosen) ?? BASEMAPS[0];
  if (!wanted.proxied) return wanted.id;
  if (serverLayers == null) return null;
  const layer = serverLayers?.find((l) => l.id === wanted.id);
  return layer?.available ? wanted.id : "osm";
}

/**
 * Hakee yhden palvelimen kautta kulkevan karttaruudun kuvana. Käytetään sekä
 * näyttöön että offline-esilataukseen. Leafletin tavallinen <img> ei tue
 * valtuutusotsaketta, joten haku tehdään fetchillä: salainen API-avain ei
 * päädy URL-osoitteisiin tai palvelimen access-lokeihin.
 */
export async function fetchTileBlob(layer, z, x, y, signal) {
  const { baseUrl, apiKey } = getServerSettings();
  const response = await fetch(`${baseUrl}/api/maptiles/${layer}/${z}/${x}/${y}.png`, {
    headers: { ...(apiKey ? { "X-Api-Key": apiKey } : {}), ...sessionHeaders(serverIdentity({baseUrl})) },
    credentials: 'include',
    signal,
  });
  if (!response.ok) throw new Error(`Karttapalvelin vastasi ${response.status}`);
  return response.blob();
}

/** Leaflet-taso annetulle pohjalle. */
export function createBaseLayer(id) {
  if (id === "osm") {
    return L.tileLayer(import.meta.env.VITE_TILE_URL || "https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: import.meta.env.VITE_TILE_ATTRIBUTION || OSM_ATTRIBUTION,
    });
  }
  const { baseUrl } = getServerSettings();
  // Ruutu haetaan ensin puhelimen välimuistista (offline-kartta), sitten
  // palvelimelta, ja haettu ruutu jää talteen (lib/tileCache.js).
  const CachedTiles = L.TileLayer.extend({
    createTile(coords, done) {
      const tile = document.createElement("img");
      tile.alt = "";
      tile.setAttribute("role", "presentation");
      const controller = new AbortController();
      loadTile({
        cache: tileCache,
        layer: id,
        z: coords.z,
        x: coords.x,
        y: coords.y,
        fetchTile: (z, x, y) => fetchTileBlob(id, z, x, y, controller.signal),
      }).then(({ blob }) => {
        if (controller.signal.aborted) return;
        const objectUrl = URL.createObjectURL(blob);
        tile.onload = () => {
          URL.revokeObjectURL(objectUrl);
          done(null, tile);
        };
        tile.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          done(new Error("Karttalaatan näyttäminen epäonnistui"), tile);
        };
        tile.src = objectUrl;
      }).catch((error) => {
        if (!controller.signal.aborted) done(error, tile);
      });
      tile.addEventListener("error", () => controller.abort(), { once: true });
      return tile;
    },
  });
  return new CachedTiles(`${baseUrl}/api/maptiles/${id}/{z}/{x}/{y}.png`, {
    maxNativeZoom: MAX_NATIVE_ZOOM,
    maxZoom: 19,
    attribution: MML_ATTRIBUTION,
  });
}

